import { NgClass } from "@angular/common";
import { Component, computed, inject } from "@angular/core";
import { MapStateService } from "src/services/map/map-state.service";
import { MapTypes } from "../../../extensions/leaflet/webatlas-map-types";
import { MapActionsService } from "src/services/map/map-actions.service";
import { IMapType, IMapWmsLayer } from "@models/map/map.model";
import { ClickOutsideDirective } from "ev-directives";
import { EVToggleComponent, EVToggleStyle } from "src/app/shared-components/ev-toggle/toggle.component";
import { EvTooltipSimpleComponent } from "src/app/shared-components/ev-tooltip-simple/ev-tooltip-simple.component";
import { Router } from "@angular/router";
import { ILinkParams, LinkHelperService } from "src/services/shared/link-helper.service";
import { ExternalLinksService } from "src/services/externallinks.service";
import { PolyDrawService } from "polydraw";

@Component({
    selector: "map-navigation-settings",
    imports: [NgClass, ClickOutsideDirective, EVToggleComponent, EvTooltipSimpleComponent],
    templateUrl: "./navigation-settings.component.html",
    styleUrl: "./navigation-settings.component.scss",
})
export class NavigationSettingsComponent {
    private readonly mapActionsService = inject(MapActionsService);
    private readonly mapStateService = inject(MapStateService);
    private readonly linkHelperService = inject(LinkHelperService);
    private readonly externalLinksService = inject(ExternalLinksService);
    private readonly polydrawService = inject(PolyDrawService);
    private readonly router = inject(Router);

    settingsMenuActivated: boolean = false;
    mapTypes: MapTypes;
    wmsLayers = computed(() => this.mapActionsService.mapTypes.wmsLayers());
    ToggleStyle = EVToggleStyle;
    areaChoiceActive: boolean = true;
    anyEstateActive: boolean = true;

    addRemoveLayer(layer: IMapWmsLayer): void {
        this.mapActionsService.mapTypes.updateSelectedWmsLayer(layer);
    }

    ngOnInit() {
        this.mapTypes = this.mapActionsService.mapTypes;
    }

    openSettingsMenu() {
        this.settingsMenuActivated = !this.settingsMenuActivated;
    }

    closeSettingsMenu() {
        this.settingsMenuActivated = false;
    }

    switchMapMode(newType: IMapType): void {
        this.mapActionsService.mapTypes.setSelectedMapType(newType);
    }

    activeMapMode(): string {
        return this.mapActionsService.mapTypes.getActiveTypeName();
    }

    openMunicipalityMap(): void {
        const mapBounds = this.mapStateService.getCurrentMapBounds();
        const center = mapBounds?.bounds != null ? mapBounds.getCenter() : null;
        if (center == null) {
            return;
        }

        const link = this.linkHelperService.getMunicipalMapLink({ position: center } as ILinkParams);
        if (link?.route?.routerLink == null) {
            return;
        }

        const urlTree = this.router.createUrlTree([link.route.routerLink], { queryParams: link.route.routerLinkParams });
        if (link.route.openInNewTab) {
            window.open(this.router.serializeUrl(urlTree), "_blank");
        } else {
            this.router.navigateByUrl(urlTree);
        }

        this.closeSettingsMenu();
    }

    openTopographyMap(): void {
        const mapBounds = this.mapStateService.getCurrentMapBounds();
        const bounds = mapBounds?.bounds != null ? mapBounds.getMapBoundParams() : null;

        if (bounds == null) {
            return;
        }

        const colorize = this.activeMapMode() !== "greyTiles";
        const link = this.linkHelperService.getTopographyMapLink({ bounds, colorize } as ILinkParams);

        if (link?.route?.routerLink == null) {
            return;
        }

        const urlTree = this.router.createUrlTree([link.route.routerLink], { queryParams: link.route.routerLinkParams });
        if (link.route.openInNewTab) {
            window.open(this.router.serializeUrl(urlTree), "_blank");
        } else {
            this.router.navigateByUrl(urlTree);
        }

        this.closeSettingsMenu();
    }

    openGuide(): void {
        window.open(this.externalLinksService.getEvKartGuideUrl(), "_blank", "noopener");
        this.closeSettingsMenu();
    }

    toggleAreaChoice(): void {
        this.areaChoiceActive = !this.areaChoiceActive;
        if (this.areaChoiceActive) {
            this.polydrawService.addMarkerGroups();
        } else {
            this.polydrawService.removeMarkerGroups();
        }
    }

    toggleEveryEstate(): void {
        this.anyEstateActive = !this.anyEstateActive;
        this.mapStateService.updateAnyEstateActive(this.anyEstateActive);
    }
}
